import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import L from 'leaflet';
import { format, parseISO, isValid } from 'date-fns';

// Add these modifications to your existing NDVIChart.js component

// Update the component props to include stress view support
const NDVIChart = ({
    chartData = {},
    selectedIndices = [],
    selectedPolygon,
    activePointsData = [],
    onDateLabelClick,
    mapInstanceRef,
    isStressView = false  // New prop to indicate stress view
}) => {
    const ndviChartRef = useRef(null);
    const ndviChart = useRef(null);

    // Enhanced color mapping for stress indices
    const indexColors = {
        NDVI: 'rgba(255, 99, 71, 1)',
        NDMI: 'rgba(0, 204, 102, 1)',
        EVI: 'rgba(153, 50, 204, 1)',
        SVHI: 'rgba(0, 255, 234, 1)', // Orange for SVHI
        BIOTIC: 'rgba(220, 53, 69, 1)',    // Red for biotic stress
        ABIOTIC: 'rgba(253, 126, 20, 1)',  // Orange for abiotic stress
    };

    // Enhanced stress-specific colors
    const stressColors = {
        BIOTIC: {
            line: 'rgba(220, 53, 69, 1)',
            fill: 'rgba(220, 53, 69, 0.1)',
            point: 'rgba(220, 53, 69, 0.8)'
        },
        ABIOTIC: {
            line: 'rgba(253, 126, 20, 1)',
            fill: 'rgba(253, 126, 20, 0.1)',
            point: 'rgba(253, 126, 20, 0.8)'
        }
    };

    useEffect(() => {
        const ctx = ndviChartRef.current?.getContext('2d');
        if (ctx) {
            // Robust cleanup: destroy any existing chart on this canvas
            const existingChart = Chart.getChart(ndviChartRef.current);
            if (existingChart) {
                existingChart.destroy();
            }

            if (ndviChart.current) {
                ndviChart.current.destroy();
            }

            // Enhanced plugin for stress view
            const highlightRegionPlugin = {
                id: 'highlightRegion',
                beforeDraw(chart) {
                    const { ctx, chartArea, scales } = chart;
                    const { left, right } = chartArea;

                    if (isStressView) {
                        // Different highlighting for stress view
                        // High stress region (above 50%)
                        const yHigh = scales.y.getPixelForValue(50);
                        const yTop = chartArea.top;

                        ctx.save();
                        ctx.fillStyle = 'rgba(220, 53, 69, 0.1)'; // Light red for high stress
                        ctx.fillRect(left, yTop, right - left, yHigh - yTop);

                        // Draw horizontal line at 50%
                        ctx.strokeStyle = 'rgba(220, 53, 69, 0.6)';
                        ctx.lineWidth = 2;
                        ctx.setLineDash([8, 4]);
                        ctx.beginPath();
                        ctx.moveTo(left, yHigh);
                        ctx.lineTo(right, yHigh);
                        ctx.stroke();
                        ctx.restore();
                    } else {
                        // Original NDVI highlighting (healthy vegetation region)
                        const yMin = scales.y.getPixelForValue(0.2);
                        const yMax = scales.y.getPixelForValue(0.8);

                        ctx.save();
                        ctx.fillStyle = 'rgba(144, 238, 144, 0.2)';
                        ctx.fillRect(left, yMax, right - left, yMin - yMax);

                        ctx.strokeStyle = 'rgba(255, 0, 0, 0.8)';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        ctx.beginPath();
                        ctx.moveTo(left, yMin);
                        ctx.lineTo(right, yMin);
                        ctx.moveTo(left, yMax);
                        ctx.lineTo(right, yMax);
                        ctx.stroke();
                        ctx.restore();
                    }
                },
            };

            // Use labels from the first selected index
            const labels = selectedIndices.length > 0 ? (chartData[selectedIndices[0]]?.labels || []) : [];

            console.log(`📊 Chart Debug Info (${isStressView ? 'Stress' : 'Vegetation'} View):`);
            selectedIndices.forEach(index => {
                const data = chartData[index];
                if (data) {
                    console.log(`${index}:`, {
                        labels: data.labels?.length || 0,
                        values: data.values?.length || 0,
                        activePoints: data.activePoints?.length || 0,
                        hasNaN: data.values?.some(v => isNaN(v)),
                        valueRange: data.values?.length > 0 ?
                            `${Math.min(...data.values.filter(v => !isNaN(v)))} - ${Math.max(...data.values.filter(v => !isNaN(v)))}` : 'empty'
                    });
                }
            });

            // Create datasets with stress-specific styling
            const datasets = selectedIndices.map((index) => {
                const data = chartData[index] || { labels: [], values: [], activePoints: [] };

                console.log(`📊 Creating dataset for ${index}:`, {
                    totalPoints: data.values.length,
                    activePoints: data.activePoints.filter(Boolean).length,
                    inactivePoints: data.activePoints.filter(p => !p).length
                });

                const isStressIndex = index === 'BIOTIC' || index === 'ABIOTIC';
                const colors = isStressIndex ? stressColors[index] : {
                    line: indexColors[index],
                    fill: indexColors[index].replace('1)', '0.1)'),
                    point: indexColors[index]
                };

                return {
                    label: isStressIndex ? `${index} Stress` : index,
                    data: data.values,
                    borderColor: colors.line,
                    backgroundColor: colors.fill,
                    borderWidth: isStressIndex ? 3 : 2.5,
                    fill: isStressIndex ? true : false,

                    // Enhanced tension for stress curves
                    tension: isStressIndex ? 0.3 : 0.4,

                    // Enhanced point styling for stress
                    pointRadius: data.activePoints.map((isActive) =>
                        isStressIndex ? (isActive ? 7 : 5) : (isActive ? 6 : 4)
                    ),
                    pointStyle: data.activePoints.map((isActive) =>
                        isStressIndex ? 'rectRot' : (isActive ? 'circle' : 'triangle')
                    ),
                    pointHoverRadius: isStressIndex ? 10 : 8,

                    // Enhanced point colors for stress
                    pointBackgroundColor: data.activePoints.map((isActive) =>
                        isActive ? colors.point : 'rgba(200, 200, 200, 0.8)'
                    ),
                    pointBorderColor: data.activePoints.map((isActive) =>
                        isActive ? colors.line : 'rgba(150, 150, 150, 1)'
                    ),
                    pointBorderWidth: data.activePoints.map((isActive) => isActive ? 2 : 1),

                    // Enhanced segment styling for stress
                    segment: {
                        borderDash: function (ctx) {
                            if (!ctx || typeof ctx.p0DataIndex !== 'number' || typeof ctx.p1DataIndex !== 'number') {
                                return [];
                            }

                            const startIndex = ctx.p0DataIndex;
                            const endIndex = ctx.p1DataIndex;

                            if (startIndex >= data.activePoints.length || endIndex >= data.activePoints.length) {
                                return [];
                            }

                            const point1Active = data.activePoints[startIndex];
                            const point2Active = data.activePoints[endIndex];

                            if (!point1Active || !point2Active) {
                                return isStressIndex ? [10, 5] : [8, 4]; // Different dash pattern for stress
                            }

                            return [];
                        },

                        borderColor: function (ctx) {
                            if (!ctx || typeof ctx.p0DataIndex !== 'number' || typeof ctx.p1DataIndex !== 'number') {
                                return colors.line;
                            }

                            const startIndex = ctx.p0DataIndex;
                            const endIndex = ctx.p1DataIndex;

                            if (startIndex >= data.activePoints.length || endIndex >= data.activePoints.length) {
                                return colors.line;
                            }

                            const point1Active = data.activePoints[startIndex];
                            const point2Active = data.activePoints[endIndex];

                            if (!point1Active || !point2Active) {
                                return colors.line.replace('1)', '0.7)');
                            }
                            return colors.line;
                        },

                        borderWidth: function (ctx) {
                            if (!ctx || typeof ctx.p0DataIndex !== 'number' || typeof ctx.p1DataIndex !== 'number') {
                                return isStressIndex ? 3 : 2.5;
                            }

                            const startIndex = ctx.p0DataIndex;
                            const endIndex = ctx.p1DataIndex;

                            if (startIndex >= data.activePoints.length || endIndex >= data.activePoints.length) {
                                return isStressIndex ? 3 : 2.5;
                            }

                            const point1Active = data.activePoints[startIndex];
                            const point2Active = data.activePoints[endIndex];

                            return (!point1Active || !point2Active) ?
                                (isStressIndex ? 2.5 : 2) : (isStressIndex ? 3 : 2.5);
                        }
                    },

                    // Enhanced animation
                    animation: {
                        duration: isStressIndex ? 1500 : 1000,
                        easing: 'easeInOutQuart'
                    }
                };
            });

            // Initialize the Chart with enhanced options for stress view
            ndviChart.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets,
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,

                    animation: {
                        duration: isStressView ? 1500 : 1200,
                        easing: 'easeInOutCubic'
                    },

                    interaction: {
                        intersect: false,
                        mode: 'index',
                    },

                    scales: {
                        x: {
                            ticks: {
                                callback: (value, index) => {
                                    // Use labels from closure to avoid accessing ndviChart.current before initialization
                                    const originalLabel = labels[index];
                                    if (!originalLabel) return '';

                                    try {
                                        const parsedDate = parseISO(originalLabel);
                                        if (isValid(parsedDate)) {
                                            return format(parsedDate, 'dd-MMM');
                                        }
                                    } catch (error) {
                                        console.warn(`Invalid date: ${originalLabel}`);
                                    }
                                    return '';
                                },
                                maxRotation: 45,
                                minRotation: 45,
                                maxTicksLimit: 10
                            },
                            grid: {
                                color: isStressView ? 'rgba(220, 53, 69, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                                lineWidth: 1
                            },
                            title: {
                                display: isStressView,
                                text: 'Date',
                                color: '#495057',
                                font: {
                                    weight: 'bold'
                                }
                            }
                        },
                        y: {
                            beginAtZero: true,
                            max: isStressView ? 100 : undefined, // Stress percentages go to 100%
                            ticks: {
                                stepSize: isStressView ? 10 : 0.1,
                                callback: function (value) {
                                    return isStressView ? `${value}%` : value.toFixed(1);
                                }
                            },
                            grid: {
                                color: isStressView ? 'rgba(220, 53, 69, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                                lineWidth: 1
                            },
                            title: {
                                display: isStressView,
                                text: 'Stress Level (%)',
                                color: '#495057',
                                font: {
                                    weight: 'bold'
                                }
                            }
                        },
                    },

                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            labels: {
                                generateLabels: function (chart) {
                                    const original = Chart.defaults.plugins.legend.labels.generateLabels;
                                    const labels = original.call(this, chart);

                                    if (isStressView) {
                                        // Add stress-specific legend items
                                        labels.push(
                                            {
                                                text: '◆ High Stress Zone (>50%)',
                                                fillStyle: 'rgba(220, 53, 69, 0.3)',
                                                strokeStyle: 'rgba(220, 53, 69, 0.6)',
                                                lineWidth: 2,
                                                lineDash: [8, 4],
                                                hidden: false
                                            }
                                        );
                                    } else {
                                        // Add original vegetation legend items
                                        labels.push(
                                            {
                                                text: '● Satellite Data',
                                                fillStyle: 'rgba(75, 192, 192, 1)',
                                                strokeStyle: 'rgba(75, 192, 192, 1)',
                                                lineWidth: 2,
                                                pointStyle: 'circle',
                                                hidden: false,
                                                lineDash: []
                                            },
                                            {
                                                text: '▲ Interpolated',
                                                fillStyle: 'rgba(200, 200, 200, 0.8)',
                                                strokeStyle: 'rgba(150, 150, 150, 1)',
                                                lineWidth: 2,
                                                lineDash: [8, 4],
                                                pointStyle: 'triangle',
                                                hidden: false
                                            }
                                        );
                                    }

                                    return labels;
                                },
                                usePointStyle: true,
                                padding: 15
                            }
                        },

                        tooltip: {
                            backgroundColor: isStressView ? 'rgba(220, 53, 69, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                            titleColor: 'white',
                            bodyColor: 'white',
                            borderColor: isStressView ? 'rgba(220, 53, 69, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                            borderWidth: 1,
                            cornerRadius: 8,
                            displayColors: true,
                            callbacks: {
                                afterLabel: function (context) {
                                    if (isStressView) {
                                        const value = context.parsed.y;
                                        if (value > 70) {
                                            return ['🚨 Critical stress level', '⚠️ Immediate attention required'];
                                        } else if (value > 50) {
                                            return ['⚠️ High stress detected', '📈 Monitor closely'];
                                        } else if (value > 25) {
                                            return ['⚠️ Moderate stress', '📊 Normal monitoring'];
                                        } else {
                                            return ['✅ Low stress level', '🌱 Healthy condition'];
                                        }
                                    } else {
                                        const datasetIndex = context.datasetIndex;
                                        const dataIndex = context.dataIndex;
                                        const dataset = chartData[selectedIndices[datasetIndex]];

                                        if (!dataset.activePoints[dataIndex]) {
                                            return ['⚠️ Cloud cover - interpolated value', '📈 Based on nearby clear dates'];
                                        }

                                        return '✅ Satellite measurement';
                                    }
                                },
                                title: function (context) {
                                    const originalTitle = context[0].label;
                                    try {
                                        const parsedDate = parseISO(originalTitle);
                                        if (isValid(parsedDate)) {
                                            return format(parsedDate, 'EEEE, MMM dd, yyyy');
                                        }
                                    } catch (error) {
                                        console.warn(`Invalid date: ${originalTitle}`);
                                    }
                                    return originalTitle;
                                },
                                label: function (context) {
                                    const value = context.parsed.y;
                                    const suffix = isStressView ? '%' : '';
                                    const precision = isStressView ? 1 : 3;
                                    return `${context.dataset.label}: ${value.toFixed(precision)}${suffix}`;
                                }
                            }
                        },

                        // Enhanced title for stress view
                        title: {
                            display: isStressView,
                            text: 'Crop Stress Analysis Over Time',
                            color: '#495057',
                            font: {
                                size: 16,
                                weight: 'bold'
                            },
                            padding: 20
                        }
                    },

                    // Enhanced click handling for stress view
                    onClick: (evt) => {
                        const activePoints = ndviChart.current.getElementsAtEventForMode(
                            evt, 'nearest', { intersect: true }, true
                        );

                        if (activePoints.length > 0) {
                            const datasetIndex = activePoints[0].datasetIndex;
                            const index = activePoints[0].index;
                            const clickedIndex = selectedIndices[datasetIndex];
                            const selectedData = chartData[clickedIndex];
                            const clickedDate = labels[index];

                            if (isStressView) {
                                // Handle stress view clicks
                                const stressValue = selectedData.values[index];
                                showStressDetailPopup(clickedDate, clickedIndex, stressValue);
                            } else {
                                // Handle regular index view clicks
                                const image = selectedData.images[index];
                                const isActive = selectedData.activePoints[index];

                                if (image) {
                                    if (!isActive || image.cloud_cover) {
                                        showCloudCoverNotification(clickedDate);
                                    } else if (isActive && image.image_url && selectedPolygon) {
                                        toggleNDVIImage(image.image_url, selectedPolygon);
                                    }
                                } else {
                                    showCloudCoverNotification(clickedDate);
                                }
                            }
                        } else {
                            const elements = ndviChart.current.getElementsAtEventForMode(evt, 'index', { intersect: false }, true);
                            if (elements.length > 0) {
                                const clickedDate = ndviChart.current.data.labels[elements[0].index];
                                if (!isStressView) {
                                    handleDateLabelClick(clickedDate);
                                }
                            }
                        }
                    },

                    onHover: (evt, elements) => {
                        ndviChartRef.current.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                    },
                },
                plugins: [highlightRegionPlugin],
            });
        }

        return () => {
            if (ndviChart.current) {
                ndviChart.current.destroy();
            }
        };
    }, [chartData, selectedIndices, activePointsData, isStressView]);

    const handleDateLabelClick = (clickedDate) => {
        console.log("Clicked on date label:", clickedDate);

        if (typeof onDateLabelClick === "function") {
            onDateLabelClick(clickedDate);
        } else {
            console.warn("onDateLabelClick is not a valid function.");
        }
    };

    const showStressDetailPopup = (date, stressType, value) => {
        if (mapInstanceRef && mapInstanceRef.current) {
            const stressLevel = value > 70 ? 'Critical' : value > 50 ? 'High' : value > 25 ? 'Moderate' : 'Low';
            const stressColor = value > 70 ? '#dc3545' : value > 50 ? '#fd7e14' : value > 25 ? '#ffc107' : '#28a745';

            const popup = L.popup()
                .setLatLng(mapInstanceRef.current.getCenter())
                .setContent(`
                    <div style="text-align: center; padding: 15px; background: #f8f9fa; border-radius: 10px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);">
                        <h4 style="margin: 0 0 12px 0; color: ${stressColor};">
                            <span style="font-size: 24px;">${stressType === 'BIOTIC' ? '🦠' : '💧'}</span> ${stressType} Stress
                        </h4>
                        <p style="margin: 8px 0; font-weight: bold; color: #333;">Date: ${date}</p>
                        <div style="margin: 12px 0; padding: 10px; background: ${stressColor}; border-radius: 6px; color: white;">
                            <strong>${value.toFixed(1)}% - ${stressLevel} Stress</strong>
                        </div>
                        <p style="margin: 8px 0; font-size: 13px; color: #666;">
                            ${stressType === 'BIOTIC' ? 'Related to crop health and vegetation condition' : 'Related to water availability and moisture stress'}
                        </p>
                        <div style="margin-top: 12px; padding: 8px; background: #e9ecef; border-radius: 6px;">
                            <small style="color: #495057;">
                                ${value > 50 ? '<strong>Action needed:</strong> Monitor field conditions closely' : 'Continue regular monitoring'}
                            </small>
                        </div>
                    </div>
                `)
                .openOn(mapInstanceRef.current);
        } else {
            alert(`${stressType} Stress - ${date}\n\nStress Level: ${value.toFixed(1)}%`);
        }
    };

    const showCloudCoverNotification = (date) => {
        if (mapInstanceRef && mapInstanceRef.current) {
            const popup = L.popup()
                .setLatLng(mapInstanceRef.current.getCenter())
                .setContent(`
                    <div style="text-align: center; padding: 15px; background: #f8f9fa; border-radius: 10px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);">
                        <h4 style="margin: 0 0 12px 0; color: #ff6b6b;">
                            <span style="font-size: 24px;">☁️</span> Cloud Cover
                        </h4>
                        <p style="margin: 8px 0; font-weight: bold; color: #333;">Date: ${date}</p>
                        <p style="margin: 8px 0; font-size: 13px; color: #666;">
                            Satellite imagery unavailable due to cloud cover.
                            <br/>Value interpolated from nearby clear dates.
                        </p>
                        <div style="margin-top: 12px; padding: 8px; background: #e9ecef; border-radius: 6px;">
                            <small style="color: #495057;">
                                <strong>Dashed lines</strong> indicate interpolated data
                            </small>
                        </div>
                    </div>
                `)
                .openOn(mapInstanceRef.current);
        } else {
            alert(`☁️ Cloud Cover - ${date}\n\nValue interpolated from nearby clear dates.`);
        }
    };

    const toggleNDVIImage = (imageUrl, layer) => {
        if (!imageUrl || !layer) {
            console.error("Image URL or layer is undefined");
            return;
        }

        const bounds = layer.getBounds();
        if (!bounds || !bounds.isValid() || !layer._map) {
            console.error("Invalid bounds or layer not associated with map");
            return;
        }

        // Toggle overlay visibility
        if (layer.overlay) {
            layer.overlay.remove();
            layer.overlay = null;
        } else {
            const overlay = L.imageOverlay(imageUrl, bounds, { opacity: 1 }).addTo(layer._map);
            overlay.setZIndex(100);
            layer.overlay = overlay;
        }
    };

    // Enhanced chart container with stress-specific styling
    const chartContainerStyle = {
        width: '100%',
        minHeight: '180px',
        height: '22vh',
        flex: 1,
        marginTop: '-5px',
        backgroundColor: isStressView ? '#fef7f7' : 'transparent',
        borderRadius: '8px',
        boxShadow: isStressView ?
            '0 2px 8px rgba(220, 53, 69, 0.15)' : 'none',
        border: isStressView ? '1px solid rgba(220, 53, 69, 0.2)' : 'none',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
    };

    return (
        <div style={chartContainerStyle}>
            {isStressView && (
                <div style={{
                    position: 'absolute',
                    top: '1px !important',
                    right: '15px !important',
                    background: 'rgba(220, 53, 69, 0.1) !important',
                    padding: '0px 10px !important',
                    borderRadius: '15px !important',
                    fontSize: '12px !important',
                    fontWeight: '500 !important',
                    color: '#721c24 !important',
                    zIndex: '10 !important'
                }}>
                    🔬 Stress Analysis Mode
                </div>
            )}
            <canvas
                ref={ndviChartRef}
                style={{
                    width: '100%',
                    height: '100%'
                }}
            />
        </div>
    );
};

export default NDVIChart;